import { Box, Drawer, Theme, useMediaQuery } from '@mui/material';
import { useContext, useState } from 'react';
import { useSearchParams } from 'react-router';

import { Scrollbar } from '@components/Scrollbar';
import { CaptureContext } from '@contexts/CaptureContext';
import { CaptureOptions } from '@rdTypes/capture';
import { sxScrollWrapper } from 'src/theme/shared/scrollbar';

import { DialogDarkFullscreen } from '../DialogDarkFullscreen';
import ButtonsList from './components/ButtonsList';
import Dialog from './components/Dialog';
import Header from './components/Header';
import { useCapture } from './hooks/useCapture';
import { styles, sxMobileGroupBgColor, sxMobileGroupBtn } from './styles';
import { DialogProcess, Steps } from './types';

type CaptureContentProps = {
  onClose: () => void;
  options: CaptureOptions;
};

const CaptureContent = ({ onClose, options }: CaptureContentProps) => {
  const [dialogState, setDialogState] = useState<DialogProcess | null>(null);
  const isMobileView = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const {
    getButtonsByState,
    getTitleByState,
    getComponentByState,
    getIconsByState,
    getDrawerContent,
    setDrawer,
    isUnresolvedPieces,
    isUploadingPieces,
    captureState,
  } = useCapture(options);

  const activeComponent = getComponentByState();
  const { Component, props, onClose: componentCloseHandler } = activeComponent || {};

  const handleCaptureClose = (isFromCloseDialog?: boolean) => {
    if (isUploadingPieces()) {
      return setDialogState(DialogProcess.Uploading);
    }

    if (isUnresolvedPieces()) {
      return setDialogState(isFromCloseDialog ? DialogProcess.UnresolvedByClose : DialogProcess.Unresolved);
    }

    onClose();
    return true;
  };

  const actionButtons = getButtonsByState(handleCaptureClose);
  const { Component: DrawerComponent, props: drawerComponentProps } = getDrawerContent();

  const handleModalClose = () => {
    handleCaptureClose(true);
  };

  return (
    <DialogDarkFullscreen open onClose={handleModalClose}>
      <Header
        title={getTitleByState()}
        icons={getIconsByState()}
        onClose={componentCloseHandler || handleModalClose}
        buttons={actionButtons}
        captureState={captureState}
      />
      {Component && <Component {...props} />}
      {isMobileView && captureState !== Steps.Photo && actionButtons.length > 0 ? (
        <Box
          sx={{
            ...sxMobileGroupBtn,
            ...(sxMobileGroupBgColor[captureState as Steps.Images | Steps.ReviewItems] || {}),
          }}
        >
          <ButtonsList buttons={actionButtons} />
        </Box>
      ) : null}
      <Drawer
        open={!!DrawerComponent}
        sx={styles.sxDrawer}
        anchor="right"
        slotProps={{
          paper: styles.sxDrawerPaper,
        }}
        onClose={() => setDrawer(null)}
        disablePortal
        id='barcode-reader-listener-id'
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
      >
        <Scrollbar sx={sxScrollWrapper}>{DrawerComponent && <DrawerComponent {...drawerComponentProps} />}</Scrollbar>
      </Drawer>
      <Dialog className="dark" dialog={dialogState} setDialog={setDialogState} handleClose={onClose} />
    </DialogDarkFullscreen>
  );
};

const Capture = () => {
  const [urlParams] = useSearchParams();
  const sessionId = urlParams.get('sessionId');
  const { setClose, options } = useContext(CaptureContext);

  return sessionId ? <CaptureContent onClose={setClose} options={options} /> : null;
};

export default Capture;
